import "primevue/resources/primevue.min.css";
import "primeflex/primeflex.css";
import "primeicons/primeicons.css";
import "prismjs/themes/prism-coy.css";
import "./assets/styles/layout.scss";

import { createApp, reactive } from "vue";
import { createPinia } from "pinia";
import router from "./router";
import AppWrapper from "./AppWrapper.vue";
import PrimeVue from "primevue/config";
import AutoComplete from "primevue/autocomplete";
import Badge from "primevue/badge";
import Button from "primevue/button";
import Breadcrumb from "primevue/breadcrumb";
import Calendar from "primevue/calendar";
import Card from "primevue/card";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import ConfirmDialog from "primevue/confirmdialog";
import ConfirmPopup from "primevue/confirmpopup";
import ConfirmationService from "primevue/confirmationservice";
import ContextMenu from "primevue/contextmenu";
import ColumnGroup from "primevue/columngroup";
import DataTable from "primevue/datatable";
import Dialog from "primevue/dialog";
import Divider from "primevue/divider";
import Dropdown from "primevue/dropdown";
import FileUpload from "primevue/fileupload";
import Image from "primevue/image";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import InputSwitch from "primevue/inputswitch";
import Menu from "primevue/menu";
import Menubar from "primevue/menubar";
import Message from "primevue/message";
import MultiSelect from "primevue/multiselect";
import Paginator from "primevue/paginator";
import PanelMenu from "primevue/panelmenu";
import Password from "primevue/password";
import Ripple from "primevue/ripple";
import RadioButton from "primevue/radiobutton";
import Row from "primevue/row"
import StyleClass from "primevue/styleclass";
import Textarea from "primevue/textarea";
import Toast from "primevue/toast";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";
import TreeSelect from "primevue/treeselect";
import ProgressSpinner from 'primevue/progressspinner';
import Panel from 'primevue/panel';
import Chart from 'primevue/chart';
import Tag from "primevue/tag";

import CodeHighlight from "./AppCodeHighlight";

router.beforeEach(function (to, from, next) {
  window.scrollTo(0, 0);
  next();
});

const pinia = createPinia();
const app = createApp(AppWrapper);

app.config.globalProperties.$appState = reactive({
  theme: "saga-blue",
  darkTheme: false,
});

app.use(pinia);
app.use(PrimeVue, {
  ripple: true,
  inputStyle: "outlined",
  locale: {
    dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
    dayNames: ["Domingo", "Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado"],
    monthNames: ["Enero", 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    accept: 'Aceptar',
    reject: 'Rechazar',
    clear: 'Limpiar',
    apply: 'Buscar',
    choose: 'Seleccionar',
    upload: 'Cargar',
    cancel: 'Cancelar',
    completed: 'Completado',
    startsWith: 'Empieza con',
    contains: 'Contiene',
    notContains: 'No contiene',
    endsWith: 'Termina con',
    equals: 'Igual',
    notEquals: 'No igual',
    noFilter: 'Sin filtro',
    aria: {
      trueLabel: 'True',
      falseLabel: 'False',
      nullLabel: 'Not Selected',
      star: '1 star',
      stars: '{star} stars',
      selectAll: 'All items selected',
      unselectAll: 'All items unselected',
      close: 'Cerrar',
      previous: 'Anterior',
      next: 'Siguiente',
      navigation: 'Navigation',
      scrollTop: 'Scroll Top',
      moveTop: 'Move Top',
      moveUp: 'Move Up',
      moveDown: 'Move Down',
      moveBottom: 'Move Bottom',
      moveToTarget: 'Move to Target',
      moveToSource: 'Move to Source',
      moveAllToTarget: 'Move All to Target',
      moveAllToSource: 'Move All to Source',
      pageLabel: '{page}',
      firstPageLabel: 'First Page',
      lastPageLabel: 'Last Page',
      nextPageLabel: 'Next Page',
      prevPageLabel: 'Previous Page',
      rowsPerPageLabel: 'Rows per page',
      previousPageLabel: 'Previous Page',
      jumpToPageDropdownLabel: 'Jump to Page Dropdown',
      jumpToPageInputLabel: 'Jump to Page Input',
      selectRow: 'Row Selected',
      unselectRow: 'Row Unselected',
      expandRow: 'Row Expanded',
      collapseRow: 'Row Collapsed',
      showFilterMenu: 'Show Filter Menu',
      hideFilterMenu: 'Hide Filter Menu',
      filterOperator: 'Filter Operator',
      filterConstraint: 'Filter Constraint',
      editRow: 'Row Edit',
      saveEdit: 'Save Edit',
      cancelEdit: 'Cancel Edit',
      listView: 'List View',
      gridView: 'Grid View',
      slide: 'Slide',
      slideNumber: '{slideNumber}',
      zoomImage: 'Zoom Image',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      rotateRight: 'Rotate Right',
      rotateLeft: 'Rotate Left'
  }
    // ... más configuraciones
  },
});
app.use(ConfirmationService);
app.use(ToastService);
app.use(router);
app.directive("uppercase", {
  mounted(el) {
    const input = el.tagName === "INPUT" ? el : el.querySelector("input");

    if (input) {
      input.style.textTransform = "uppercase";

      input.addEventListener("input", function (e) {
        const value = e.target.value;
        const upperValue = value.toUpperCase();

        if (value !== upperValue) {
          const start = e.target.selectionStart;
          const end = e.target.selectionEnd;
          e.target.value = upperValue;
          e.target.setSelectionRange(start, end);
        }
      });
    }
  },
});

app.directive("tooltip", Tooltip);
app.directive("ripple", Ripple);
app.directive("code", CodeHighlight);
app.directive("styleclass", StyleClass);

app.component("AutoComplete", AutoComplete);
app.component("Badge", Badge);
app.component("Breadcrumb", Breadcrumb);
app.component("Button", Button);
app.component("Calendar", Calendar);
app.component("Card", Card);
app.component("Checkbox", Checkbox);
app.component("Column", Column);
app.component("ConfirmDialog", ConfirmDialog);
app.component("ConfirmPopup", ConfirmPopup);
app.component("ContextMenu", ContextMenu);
app.component('ColumnGroup', ColumnGroup);
app.component("DataTable", DataTable);
app.component("Dialog", Dialog);
app.component("Divider", Divider);
app.component("Dropdown", Dropdown);
app.component("FileUpload", FileUpload);
app.component("Image", Image);
app.component("InputNumber", InputNumber);
app.component("InputSwitch", InputSwitch);
app.component("InputText", InputText);
app.component("Menu", Menu);
app.component("Menubar", Menubar);
app.component("Message", Message);
app.component("MultiSelect", MultiSelect);
app.component("Paginator", Paginator);
app.component("PanelMenu", PanelMenu);
app.component("Password", Password);
app.component("RadioButton", RadioButton);
app.component('Row', Row);
app.component("Textarea", Textarea);
app.component("Toast", Toast);
app.component("TreeSelect", TreeSelect);
app.component("ProgressSpinner", ProgressSpinner);
app.component("Panel", Panel);
app.component("Chart", Chart);
app.component("Tag", Tag);

app.mount("#app");